import { GanttTask } from '@crew/apis/task/models/getMyTasksForGantt/response'
import { JsonDateFormat } from '@crew/enums/system'
import dayjs from '@crew/modules'
import { useTranslation } from '@crew/modules/i18n'
import { getDefaultTabValue } from '@crew/utils/enum'
import { useEffectOnce, useFullscreen, useToggle } from '@dx-system/react-use'
import EventAvailable from '~icons/material-symbols/event-available'
import AccountTreeOutline from '~icons/material-symbols/account-tree-outline'
import BaselineDelete from '~icons/ic/baseline-delete'
import BaselineEvent from '~icons/ic/baseline-event'
import ViewAgendaOutlineSharp from '~icons/material-symbols/view-agenda-outline-sharp'
import classNames from 'classnames'
import { useModal } from 'components/layouts/modal/useModal'
import {
  Column,
  ContextMenu,
  Dependencies,
  Editing,
  Gantt,
  HeaderFilter,
  IContextMenuProps,
  Item,
  Sorting,
  Tasks,
  Toolbar,
  Validation,
} from 'devextreme-react/gantt'
import {
  CustomCommandEvent,
  GanttScaleType,
  ScaleCellPreparedEvent,
  SelectionChangedEvent,
  TaskDeletingEvent,
  TaskEditDialogShowingEvent,
  TaskInsertingEvent,
  TaskUpdatingEvent,
} from 'devextreme/ui/gantt'
import { NotifyEventType, TaskDetailListTabs } from 'enums/app'
import { TaskEntryDialog } from 'features/task/components/taskEntryDialog/taskEntryDialog'
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useMyTaskListGantt } from './useMyTaskListGantt'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import {
  adjustTimeAndFormatEndDate,
  adjustTimeAndFormatStartDate,
} from 'utils/date'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewBadge } from 'components/elements/crewBadge/crewBadge'
import { useValueChangeEffect } from '@crew/hooks'
import { useAppSelector } from 'states/hooks'
import { convertMinutesToHHMM } from '@crew/utils'
import { cloneDeep } from 'lodash'
import { CrewGantt } from 'components/devextreme/crewGantt'
import { TaskEntryFormMode } from 'features/task/components/taskEntryDialog/components/taskEntryForm/taskEntryForm'
import { useTaskDependencyForMyTaskDataSource } from 'hooks/dataSource/useTaskDependencyForMyTaskDataSource'

const ScaleTypes: GanttScaleType[] = [
  'days',
  'weeks',
  'months',
  'quarters',
  'years',
] as const

// デフォルトのタスクリストの幅は500pxで、件名、開始日、期限日の列が表示される
const DEFAULT_TASK_LIST_WIDTH = 500

// 詳細表示時のタスクリストの幅は1330pxで、すべての列が表示される
const DETAIL_TASK_LIST_WIDTH = 1330

// 詳細表示時の「タスクリストの幅 / ガントチャートの幅」の比率
const TASK_LIST_WIDTH_WITH_DETAIL_COLUMN_RATIO = 0.8

// 日付フォーマット関数
// cellRenderの型のparamsがany型のためany型を使用
const dateFormatter = (date: any): React.ReactNode => {
  // check date.value is Invalid Date
  if (!date.value || isNaN(date.value)) {
    return ''
  }

  return dayjs(date.value).format('YYYY/MM/DD')
}

// 進捗率列のレンダラ
const renderProgressRate = ({
  value,
}: {
  value: number | null
}): React.ReactNode => {
  if (value === null || value === undefined) return ''

  return `${value}%`
}

// タスク状態列のレンダラ
const renderTaskState = ({ data }: { data: GanttTask }): React.ReactNode => {
  return (
    <div className="w-full text-center">
      <CrewBadge displayColor={data.taskState.displayColor}>
        {data.taskState.name}
      </CrewBadge>
    </div>
  )
}

// タスク状態セルの値計算
const calculateTaskStateCellValue = (rowData: GanttTask) => {
  return rowData.taskState?.name ?? ''
}

// 予定作業時間セルの値計算
const calculateEstimatedWorkTimesCellValue = (rowData: GanttTask) => {
  return typeof rowData.estimatedWorkTimes === 'number'
    ? convertMinutesToHHMM(rowData.estimatedWorkTimes)
    : ''
}

// 実績作業時間セルの値計算
const calculateActualWorkTimesCellValue = (rowData: GanttTask) => {
  return typeof rowData.actualWorkTimes === 'number'
    ? convertMinutesToHHMM(rowData.actualWorkTimes)
    : ''
}

export const MyTaskListGantt: FC = memo(() => {
  const {
    ganttTaskDataSource,
    updateGanttTask,
    deleteTask,
    findRelatedTasks,
    updateRelatedTasksByTargetTask,
    hasChildTask,
  } = useMyTaskListGantt()

  const { t } = useTranslation()
  const [showApiErrors] = useShowApiErrors()
  const GanttRef = useRef<Gantt>(null)
  const navigate = useNavigate()

  // タスク登録・編集ダイアログの状態管理
  const [isTaskEntryDialogOpen, openTaskEntryDialog, closeTaskEntryDialog] =
    useModal()

  // 確認ダイアログの状態管理
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useModal()
  const [confirmMessage, setConfirmMessage] = useState('')

  // 処理対象のタスクIDを保持するローカルステート
  const [executionTargetTaskId, setExecutionTargetTaskId] = useState<string>()

  // タスク登録ダイアログを開く際のプロジェクトIDを保持するローカルステート
  const [projectIdForTaskEntryDialog, setProjectIdForTaskEntryDialog] =
    useState<string>()

  const [parentTaskId, setParentTaskId] = useState<string>()
  const [taskEntryFormMode, setTaskEntryFormMode] = useState<TaskEntryFormMode>(
    TaskEntryFormMode.New
  )

  // 詳細表示を行うかどうかを保持するローカルステート
  const [isViewDetailColumn, setIsViewDetailColumn] = useState(false)

  // タスクイベントメッセージ
  const taskEventMessage = useAppSelector((state) => state.app.taskEventMessage)
  // ログイン中ユーザー情報
  const loggedInUser = useAppSelector((state) => state.app.loggedInUser)

  // タスク依存関係のデータソース
  const taskDependencyDataSource = useTaskDependencyForMyTaskDataSource()

  // 選択行を保持するローカルステート
  const [selectedRow, setSelectedRow] = useState<string>()

  // ガントチャートのタスクデータソースを保持するref
  // 更新前後でタスクデータを維持しておく必要があるため、useRefで保持する
  const ganttTasksRef = useRef<GanttTask[]>([])

  // 全画面表示で使用する変数群
  const [ganttWidth, setGanttWidth] = useState<number>()
  const ganttChartContainerRef = useRef(null)
  const [enableFullScreen, setEnableFullScreen] = useToggle(false)

  // タスク登録ダイアログ表示時の処理
  // デフォルトだとDevExtremeのダイアログが表示されるため、これをキャンセルして独自のダイアログを表示する
  const handleTaskInsertDialogShowing = useCallback(
    (event: TaskInsertingEvent) => {
      // ガントチャートのデフォルトで表示されるダイアログが表示されないようキャンセルする
      event.cancel = true

      // タスク編集ではないのでundefinedを設定
      setExecutionTargetTaskId(undefined)
      // 新規登録なのでダイアログでプロジェクトIDが設定されないようundefinedを設定
      setProjectIdForTaskEntryDialog(undefined)
      // 選択しているタスクと同階層にタスクを作るため、同じ親タスクIDを設定
      setParentTaskId(event.values.parentTaskId)
      // 新規登録モードを設定
      setTaskEntryFormMode(TaskEntryFormMode.New)

      // 独自のダイアログを開く
      openTaskEntryDialog()
    },
    [openTaskEntryDialog]
  )

  // タスク編集ダイアログ表示時の処理
  // デフォルトだとDevExtremeのダイアログが表示されるため、これをキャンセルして独自のダイアログを表示する
  const handleTaskEditDialogShowing = useCallback(
    (event: TaskEditDialogShowingEvent) => {
      // ガントチャートのデフォルトで表示されるダイアログが表示されないようキャンセルする
      event.cancel = true

      // 編集するタスクIDを設定
      setExecutionTargetTaskId(event.key)
      // 新規登録なのでダイアログでプロジェクトIDが設定されないようundefinedを設定
      setProjectIdForTaskEntryDialog(undefined)
      // 選択しているタスクの親タスクIDは変更しないので、その親タスクIDを設定
      setParentTaskId(event.values.parentTaskId)
      // 編集モードを設定
      setTaskEntryFormMode(TaskEntryFormMode.Edit)

      // 独自のダイアログを開く
      openTaskEntryDialog()
    },
    [openTaskEntryDialog]
  )

  // タスク登録・編集ダイアログのsubmit処理
  const handleTaskEntryDialogSubmit = useCallback(() => {
    closeTaskEntryDialog()
  }, [closeTaskEntryDialog])

  // タスク編集ダイアログでタスクが削除されたときの処理
  const handleTaskDeleted = useCallback(
    (taskId: string) => {
      closeTaskEntryDialog()
    },
    [closeTaskEntryDialog]
  )

  // レイアウトの変更を監視して、ガントチャートの幅を更新する
  // ガントチャートの幅が更新された後、DevExtremeがセルアイテムの位置と幅を再計算する
  const ganttWrapperNodeRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const node = ganttWrapperNodeRef.current
    if (!node) return

    const resizeObserver = new ResizeObserver(() => {
      setGanttWidth(node.offsetWidth)
    })
    resizeObserver.observe(node)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // スケールに応じてヘッダセルの表示を制御するハンドラ
  const handleScaleCellPrepared = useCallback(
    (event: ScaleCellPreparedEvent) => {
      const scaleElement = event.scaleElement
      scaleElement.style.setProperty('padding-left', '0')
      scaleElement.style.setProperty('padding-right', '0')

      if (
        event.scaleType === 'weeks' &&
        !isNaN(event.startDate.getTime()) &&
        !isNaN(event.endDate.getTime())
      ) {
        scaleElement.textContent = `${t('format.shortDateWithText', {
          value: event.startDate,
        })} - ${t('format.shortDateWithText', {
          value: event.endDate,
        })}`
      } else if (
        event.scaleType === 'days' &&
        !isNaN(event.startDate.getTime())
      ) {
        scaleElement.textContent = t('format.dayOfWeek', {
          value: event.startDate,
        })
      }
    },
    [t]
  )

  // 全画面表示終了（ESCボタン押下時）
  const handleExitFullScreen = useCallback(() => {
    setEnableFullScreen(false)
  }, [setEnableFullScreen])

  // 全画面表示ボタンがクリックされたときのイベントハンドラ
  const handleFullScreenButtonClick = useCallback(() => {
    setEnableFullScreen(true)
  }, [setEnableFullScreen])

  // ズームインボタンクリック時のイベントハンドラ
  const handleZoomInButtonClick = useCallback(() => {
    const currentScaleType =
      GanttRef.current?.instance.option('scaleType') ?? 'days'

    const currentScaleTypeIndex = ScaleTypes.indexOf(currentScaleType)

    // If the current scale type is the last one, do nothing
    if (currentScaleTypeIndex === 0) return

    const nextScaleType = ScaleTypes[currentScaleTypeIndex - 1]

    if (nextScaleType === 'days') {
      // Zoom in 3 times to change narrowest display
      GanttRef.current?.instance.zoomIn()
      GanttRef.current?.instance.zoomIn()
      GanttRef.current?.instance.zoomIn()
    } else if (nextScaleType === 'weeks') {
      // Zoom in 1 times to change narrowest display
      GanttRef.current?.instance.zoomIn()
    }

    // Set the next scale type
    GanttRef.current?.instance.option('scaleType', nextScaleType)
  }, [])

  // ズームアウトボタンクリック時のイベントハンドラ
  const handleZoomOutButtonClick = useCallback(() => {
    const currentScaleType =
      GanttRef.current?.instance.option('scaleType') ?? 'days'

    const currentScaleTypeIndex = ScaleTypes.indexOf(currentScaleType)

    // If the current scale type is the last one, do nothing
    if (currentScaleTypeIndex === ScaleTypes.length - 1) return

    const nextScaleType = ScaleTypes[currentScaleTypeIndex + 1]
    if (nextScaleType === 'months') {
      // Zoom out 1 times to change widest display
      GanttRef.current?.instance.zoomOut()
    } else if (nextScaleType === 'quarters') {
      // Zoom out 3 times to change widest display
      GanttRef.current?.instance.zoomOut()
      GanttRef.current?.instance.zoomOut()
      GanttRef.current?.instance.zoomOut()
    } else if (nextScaleType === 'years') {
      // Zoom out 3 times to change widest display
      GanttRef.current?.instance.zoomOut()
      GanttRef.current?.instance.zoomOut()
      GanttRef.current?.instance.zoomOut()
    }

    // Set the next scale type
    GanttRef.current?.instance.option('scaleType', nextScaleType)
  }, [])

  // ガントチャートに全画面表示ボタンを表示するオプション
  const fullScreenButtonOptions = {
    icon: 'fullscreen',
    stylingMode: 'text',
    hint: t('label.fullScreen'),
    onClick: handleFullScreenButtonClick,
  }

  // ズームインボタンのオプション
  const zoomInButtonOptions = {
    icon: 'dx-icon dx-gantt-i dx-gantt-i-zoom-in',
    stylingMode: 'text',
    hint: t('label.zoomIn'),
    onClick: handleZoomInButtonClick,
  }

  // ズームアウトボタンのオプション
  const zoomOutButtonOptions = {
    icon: 'dx-icon dx-gantt-i dx-gantt-i-zoom-out',
    stylingMode: 'text',
    hint: t('label.zoomOut'),
    onClick: handleZoomOutButtonClick,
  }

  // ツールバーの「サブタスクを追加」ボタンクリック時のイベントハンドラ
  const handleAddSubTaskButtonOnToolbarClick = useCallback(() => {
    // タスクが選択状態ではない場合、サブタスク追加はできないため何もしない
    if (selectedRow === undefined) {
      return
    }

    // 選択行のタスクデータを取得
    const selectedTask = GanttRef.current?.instance.getTaskData(selectedRow)

    setExecutionTargetTaskId(undefined)
    // 選択行のタスクと同じプロジェクトにタスクが作られるよう、entityRecordIdを設定
    setProjectIdForTaskEntryDialog(selectedTask?.entityRecordId)
    setParentTaskId(selectedRow)
    // 新規登録モードを設定
    setTaskEntryFormMode(TaskEntryFormMode.New)

    // ダイアログを開く
    openTaskEntryDialog()
  }, [openTaskEntryDialog, selectedRow])

  // ツールバーの「タスクを追加」ボタンクリック時のイベントハンドラ
  const handleAddTaskButtonOnToolbarClick = useCallback(() => {
    setExecutionTargetTaskId(undefined)

    // 新規登録なのでダイアログでプロジェクトIDが設定されないようundefinedを設定
    setProjectIdForTaskEntryDialog(undefined)

    // 選択行のタスクデータを取得
    const selectedTask = GanttRef.current?.instance.getTaskData(selectedRow)
    // 選択されたタスクがある場合は、そのタスクと同じ階層に新規タスクを作成する
    setParentTaskId(selectedTask?.parentTaskId ?? undefined)

    // 新規登録モードを設定
    setTaskEntryFormMode(TaskEntryFormMode.New)

    openTaskEntryDialog()
  }, [openTaskEntryDialog, selectedRow])

  // ツールバーの「タスクを削除」ボタンクリック時のイベントハンドラ
  const handleTaskDeleteButtonOnToolbarClick = useCallback(async () => {
    // 選択されているタスクのキーを取得
    const selectedRowKey = GanttRef.current?.instance.option('selectedRowKey')

    // 選択行がないことは通常ありえないが、ない場合は何も処理しない
    if (!selectedRowKey) {
      return
    }

    const rowData = await ganttTaskDataSource.store().byKey(selectedRowKey)

    // 選択しているタスクデータに子タスクが含まれるかどうかに応じて、確認メッセージを設定
    if (rowData.hasChildTasks) {
      setConfirmMessage(t('message.task.confirmDeleteLinkedSubtasks'))
    } else {
      setConfirmMessage(t('message.general.confirmMessage.delete'))
    }

    // 確認ダイアログを開く
    openConfirmDialog()
  }, [openConfirmDialog, t, ganttTaskDataSource])

  // ツールバーの「今日」ボタンクリック時のイベントハンドラ
  const handleShowTodayButtonOnToolbarClick = useCallback(() => {
    // Scrolls to a date of today inside Gantt chart
    GanttRef.current?.instance.scrollToDate(new Date())
  }, [])

  // ツールバーの「詳細表示」ボタンクリック時のイベントハンドラ
  const handleDetailColumnButtonOnToolbarClick = useCallback(() => {
    // toggle the visibility of the Detail column
    setIsViewDetailColumn(!isViewDetailColumn)
  }, [isViewDetailColumn])

  // タスクリスト表示幅
  const taskListWidth = useMemo(() => {
    if (!ganttWidth) return DEFAULT_TASK_LIST_WIDTH

    // ガントチャートの幅が狭い場合は、ガントチャートの幅の80%を列表示の初期幅としたい
    const calcWidth = ganttWidth * TASK_LIST_WIDTH_WITH_DETAIL_COLUMN_RATIO
    // ただし、計算結果が詳細表示の列の幅より大きくなる場合は、詳細表示の列の幅を採用する
    const detailTaskWidth =
      DETAIL_TASK_LIST_WIDTH < calcWidth ? DETAIL_TASK_LIST_WIDTH : calcWidth

    // 80% of gantt width when isViewDetailColumn is true
    return isViewDetailColumn ? detailTaskWidth : DEFAULT_TASK_LIST_WIDTH
  }, [ganttWidth, isViewDetailColumn])

  // ヘッダフィルタの日付列データをカスタマイズする関数
  // options.dataSource.postProcessの型がany型のためany型を使用
  const customizeHeaderFilterDateData = useCallback((options: any) => {
    options.dataSource.postProcess = (items: any) => {
      const newItems = items.map((item: any) => {
        return item
      })
      return newItems
    }
  }, [])

  // ツールバーの「タスクを追加」ボタンのレンダラ
  const renderAddTaskButton = useCallback(
    ({ disabled }: { disabled: boolean }) => {
      return (
        <CrewButton
          stylingMode="text"
          disabled={disabled}
          onClick={handleAddTaskButtonOnToolbarClick}
          icon={<EventAvailable width={20} height={20} />}
          text={t('action.addTask')}
        />
      )
    },
    [handleAddTaskButtonOnToolbarClick, t]
  )

  // ツールバーの「サブタスクを追加」ボタンのレンダラ
  const renderAddSubtaskButton = useCallback(() => {
    return (
      <CrewButton
        stylingMode="text"
        disabled={!selectedRow}
        onClick={handleAddSubTaskButtonOnToolbarClick}
        icon={<AccountTreeOutline width={20} height={20} />}
        text={t('action.addSubtask')}
      />
    )
  }, [handleAddSubTaskButtonOnToolbarClick, selectedRow, t])

  // ツールバーの「タスクを削除」ボタンのレンダラ
  const renderDeleteTaskButton = useCallback(() => {
    return (
      <CrewButton
        stylingMode="text"
        disabled={!selectedRow}
        onClick={handleTaskDeleteButtonOnToolbarClick}
        icon={<BaselineDelete width={20} height={20} />}
        text={t('action.deleteTask')}
      />
    )
  }, [handleTaskDeleteButtonOnToolbarClick, selectedRow, t])

  // ツールバーの「今日」ボタンのレンダラ
  const renderTodayButton = useCallback(
    ({ disabled }: { disabled: boolean }) => {
      return (
        <CrewButton
          stylingMode="text"
          disabled={disabled}
          onClick={handleShowTodayButtonOnToolbarClick}
          icon={<BaselineEvent width={20} height={20} />}
          text={t('action.toDay')}
        />
      )
    },
    [handleShowTodayButtonOnToolbarClick, t]
  )

  // ツールバーの「詳細表示」ボタンのレンダラ
  const renderDisplayDetailButton = useCallback(
    ({ disabled }: { disabled: boolean }) => {
      return (
        <CrewButton
          stylingMode="text"
          disabled={disabled}
          onClick={handleDetailColumnButtonOnToolbarClick}
          icon={<ViewAgendaOutlineSharp width={20} height={20} />}
          text={t('action.displayDetail')}
          className={classNames(
            isViewDetailColumn && '!bg-crew-gray-200 dark:!bg-crew-gray-700' // ボタンの背景色を変更
          )}
        />
      )
    },
    [handleDetailColumnButtonOnToolbarClick, isViewDetailColumn, t]
  )

  // 右クリックメニューの項目を取得する関数
  const getContextMenuItems = useCallback(
    (): IContextMenuProps['items'] => [
      'addTask',
      // Custom menu item
      // https://js.devexpress.com/jQuery/Demos/WidgetsGallery/Demo/Gantt/ContextMenu/MaterialBlueLight/
      {
        name: 'taskDetail',
        text: t('label.taskDetail'),
        icon: 'dx-icon dx-gantt-i dx-gantt-i-task-details',
      },
      {
        name: 'deleteTaskButton',
        text: t('action.deleteTask'),
        icon: 'dx-icon dx-gantt-i dx-gantt-i-delete',
      },
    ],
    [t]
  )

  // 右クリックメニューの項目がクリックされたときのイベントハンドラ
  // なお、「新しいタスクの追加」は、addTaskイベントで処理されるため、こちらで処理されることはない
  const handleContextMenuItemClick = useCallback(
    async (event: CustomCommandEvent) => {
      const selectedRowKey = event.component?.option('selectedRowKey')

      // 選択行がないことは通常ありえないが、ない場合は何も処理しない
      if (!selectedRowKey) {
        return
      }

      // 右クリックメニューから選択されたボタンに応じて処理を振り分ける
      switch (event.name) {
        case 'taskDetail':
          // 「タスクの詳細」を選択した場合の処理

          // タスク詳細画面へ遷移
          navigate(
            `/tasks/${selectedRowKey}/${getDefaultTabValue(TaskDetailListTabs)}`
          )
          break

        case 'deleteTaskButton':
          // 「タスクを削除」を選択した場合の処理

          const rowData = await ganttTaskDataSource
            .store()
            .byKey(selectedRowKey)

          // 選択しているタスクデータに子タスクが含まれるかどうかに応じて、確認メッセージを設定
          if (rowData.hasChildTasks) {
            setConfirmMessage(t('message.task.confirmDeleteLinkedSubtasks'))
          } else {
            setConfirmMessage(t('message.general.confirmMessage.delete'))
          }

          // 確認ダイアログを開く
          openConfirmDialog()
          break
        default:
          return
      }
    },
    [navigate, openConfirmDialog, t, ganttTaskDataSource]
  )

  // タスクが更新されるときのDevExtremeのガントチャートコンポーネントのイベントハンドラ
  const handleTaskUpdating = useCallback(
    async (event: TaskUpdatingEvent) => {
      try {
        // ガントチャートのデフォルトで動作する処理が行われないようキャンセルする
        event.cancel = true

        // データソースからタスクを取得
        const task = GanttRef.current?.instance.getTaskData(event.key)
        // 通常ありえないが、taskが存在しない場合はリロードだけ行う
        if (!task) {
          ganttTaskDataSource.reload()
          return
        }

        // 子タスクがある場合は、開始日と終了日を更新しない
        if (
          hasChildTask(task, ganttTasksRef.current) &&
          (event.newValues.startDate || event.newValues.dueDate)
        ) {
          return
        }

        // 設定されている場合、時間を調整して開始日をフォーマットする
        const startDate = event.newValues.startDate
          ? adjustTimeAndFormatStartDate(event.newValues.startDate)
          : task.startDate
            ? dayjs(task.startDate).format(JsonDateFormat.YYYYMMDD)
            : undefined

        // 設定されている場合、時間を調整して終了日をフォーマットする
        const dueDate = event.newValues.dueDate
          ? adjustTimeAndFormatEndDate(event.newValues.dueDate)
          : task.dueDate
            ? dayjs(task.dueDate).format(JsonDateFormat.YYYYMMDD)
            : undefined

        const updatedParams = {
          id: event.key,
          startDate: startDate
            ? dayjs(startDate).format(JsonDateFormat.YYYYMMDD)
            : undefined,
          dueDate: dueDate
            ? dayjs(dueDate).format(JsonDateFormat.YYYYMMDD)
            : undefined,
          actualProgress: event.newValues.actualProgress ?? task.actualProgress,
          version: task.version,
        }

        const updatedTask: GanttTask = {
          ...task,
          ...updatedParams,
        }

        // 関連するすべてのタスクを検索
        const relatedTasks = findRelatedTasks(event.key, ganttTasksRef.current)

        // 関連するタスクと対象タスクを更新
        const updatedTasks = updateRelatedTasksByTargetTask(
          relatedTasks,
          updatedTask,
          'update'
        )

        // ganttTasksRefを更新
        ganttTasksRef.current = ganttTasksRef.current.map((task) => {
          const updatedTask = updatedTasks.find(
            (updatedTask) => updatedTask.id === task.id
          )
          return updatedTask ?? task
        })

        // データソースのタスクを更新
        ganttTaskDataSource.store().push(
          updatedTasks.map((task) => ({
            type: 'update',
            key: task.id,
            data: {
              ...task,
              startDate: task.startDate
                ? dayjs(task.startDate).startOf('day').toDate()
                : undefined, // convert startDate to begin of day
              dueDate: task.dueDate
                ? dayjs(task.dueDate).endOf('day').toDate()
                : undefined, // convert endDate to end of day
              actualProgress: task.actualProgress,
              version: task.version + 1, // Increment the version
            },
          }))
        )

        // タスク更新処理を実行
        await updateGanttTask(updatedParams)
      } catch (error) {
        showApiErrors(error)

        // データソースをリロードして変更を元に戻す
        ganttTaskDataSource.reload()
      }
    },
    [
      findRelatedTasks,
      hasChildTask,
      showApiErrors,
      ganttTaskDataSource,
      updateGanttTask,
      updateRelatedTasksByTargetTask,
    ]
  )

  // タスクが削除されるときのDevExtremeのガントチャートコンポーネントのイベントハンドラ
  const handleTaskDeleting = useCallback(
    async (event: TaskDeletingEvent) => {
      try {
        // ガントチャートのデフォルトで動作する処理が行われないようキャンセルする
        event.cancel = true

        // データソースからタスクを取得
        const task = GanttRef.current?.instance.getTaskData(event.key)
        // 通常ありえないが、taskが存在しない場合はリロードだけ行う
        if (!task) {
          ganttTaskDataSource.reload()
          return
        }

        // 関連するすべてのタスクを検索
        const relatedTasks = findRelatedTasks(event.key, ganttTasksRef.current)

        // 関連するタスクと対象タスクを更新
        const updatedTasks = updateRelatedTasksByTargetTask(
          relatedTasks,
          task,
          'delete'
        )

        // ganttTasksRefを更新
        // 削除するタスク自身か、削除するタスクの子タスクは除外し、それ以外のタスクは更新された内容で上書きする
        ganttTasksRef.current = ganttTasksRef.current
          .filter(
            (task) => task.id !== event.key && task.parentTaskId !== event.key
          )
          .map((task) => {
            const updatedTask = updatedTasks.find(
              (updatedTask) => updatedTask.id === task.id
            )
            return updatedTask ?? task
          })

        // データソースのタスクを更新
        ganttTaskDataSource.store().push(
          updatedTasks.map((task) => ({
            type:
              // If the task is the target task or childs of the target task, remove the task
              task.id === event.key || task.parentTaskId === event.key
                ? 'remove'
                : 'update',
            key: task.id,
            data: {
              ...task,
              startDate: task.startDate
                ? dayjs(task.startDate).startOf('day').toDate()
                : null, // convert startDate to begin of day
              dueDate: task.dueDate
                ? dayjs(task.dueDate).endOf('day').toDate()
                : null, // convert endDate to end of day
              actualProgress: task.actualProgress,
              version: task.version + 1, // Increment the version
            },
          }))
        )

        // タスク削除処理を実行
        await deleteTask(event.key, task.version)
      } catch (error) {
        showApiErrors(error)

        // データソースをリロードして変更を元に戻す
        ganttTaskDataSource.reload()
      }
    },
    [
      deleteTask,
      findRelatedTasks,
      showApiErrors,
      ganttTaskDataSource,
      updateRelatedTasksByTargetTask,
    ]
  )

  // タスクの選択行変更時
  const handleSelectionChanged = useCallback((e: SelectionChangedEvent) => {
    setSelectedRow(e.selectedRowKey)
  }, [])

  // 削除確認ダイアログの「OK」ボタンクリック時のイベントハンドラ
  const handleDeletePermitButtonClick = useCallback(() => {
    // Get the selected task key
    const selectedRowKey = GanttRef.current?.instance.option('selectedRowKey')

    // Delete the task
    GanttRef.current?.instance.deleteTask(selectedRowKey)

    // 確認ダイアログを閉じる
    closeConfirmDialog()
  }, [closeConfirmDialog])

  // 全画面表示の状態を管理するカスタムフック
  const isFullScreen = useFullscreen(ganttChartContainerRef, enableFullScreen, {
    onClose: handleExitFullScreen,
  })

  // ガントチャートが表示される場合、URLパラメータが除去される
  useEffectOnce(() => {
    navigate(`?`)

    // scaleTypeを指定しない場合、scaleType = 'auto' となり、タスク開始日とタスク終了日が同一の場合、ganttの仕様から最小単位がhoursの表示になってしまう
    // また、これを防ぐためにコンポーネントにscaleType = 'days' を指定するとズームが日単位の中で繰り返されてしまうので、refから指定する
    GanttRef.current?.instance.option('scaleType', 'days')
  })

  useValueChangeEffect(
    () => {
      ganttTaskDataSource
        .store()
        .load()
        .then((items) => (ganttTasksRef.current = items as GanttTask[]))
    },
    [ganttTaskDataSource],
    ganttTaskDataSource
  )

  // タスクの登録・更新・削除のイベントが発生した際に、タスク一覧を再読み込みするための処理
  useValueChangeEffect(
    () => {
      if (!taskEventMessage) return

      if (taskEventMessage?.eventType === NotifyEventType.Inserted) {
        const targetTask = cloneDeep(taskEventMessage.object) as GanttTask

        // 担当者が自分でない場合、マイタスクではないので表示しない
        if (
          loggedInUser?.id &&
          targetTask.assignToUser?.id !== loggedInUser.id
        ) {
          return
        }

        const selectedRowKey =
          GanttRef.current?.instance.option('selectedRowKey')

        // 選択された行のインデックスを取得
        const selectedRowIndex = ganttTaskDataSource
          .items()
          .findIndex((task) => task.id === selectedRowKey)

        // フォーカスされた行のインデックスを取得
        // 対象タスクがフォーカスされた行の子でない場合、フォーカスされた行の後に新しいタスクを追加
        const targetTaskIndex =
          selectedRowIndex !== -1 && targetTask.parentTaskId !== selectedRowKey
            ? selectedRowIndex + 1
            : ganttTaskDataSource.items().length + 1

        ganttTasksRef.current = [...ganttTasksRef.current, targetTask]

        // 関連するすべてのタスクを検索
        const relatedTasks = findRelatedTasks(
          taskEventMessage.id,
          ganttTasksRef.current
        )

        // 関連するタスクと対象タスクを更新
        const updatedTasks = updateRelatedTasksByTargetTask(
          relatedTasks,
          targetTask,
          'insert'
        )

        // ganttTasksRefを更新
        ganttTasksRef.current = ganttTasksRef.current.map((task) => {
          const updatedTask = updatedTasks.find(
            (updatedTask) => updatedTask.id === task.id
          )
          return updatedTask ?? task
        })

        // データソースのタスクを更新
        ganttTaskDataSource.store().push(
          updatedTasks.map((task) => ({
            type: task.id === targetTask.id ? 'insert' : 'update',
            key: task.id,
            data: {
              ...task,
              startDate: task.startDate
                ? dayjs(task.startDate).startOf('day').toDate()
                : undefined, // 開始日を日の始まりに変換
              dueDate: task.dueDate
                ? dayjs(task.dueDate).endOf('day').toDate()
                : undefined, // 終了日を日の終わりに変換
              actualStartDate: task.actualStartDate
                ? dayjs(task.actualStartDate).toDate()
                : undefined,
              actualEndDate: task.actualEndDate
                ? dayjs(task.actualEndDate).toDate()
                : undefined,
              actualProgress: task.actualProgress ?? 0,
              version: task.version + 1, // バージョンをインクリメント
            },
            // 選択された行の後に新しいタスクを追加
            index: task.id === targetTask.id ? targetTaskIndex : undefined,
          }))
        )
      } else if (taskEventMessage.eventType === NotifyEventType.Updated) {
        // 関連するすべてのタスクを検索
        const relatedTasks = findRelatedTasks(
          taskEventMessage.id,
          ganttTasksRef.current
        )

        const targetTask = cloneDeep(taskEventMessage.object) as GanttTask

        // 更新結果で担当者が自分でなくなった場合、マイタスクではないので表示しない
        if (
          loggedInUser?.id &&
          targetTask.assignToUser?.id !== loggedInUser.id
        ) {
          const updatedTasks = updateRelatedTasksByTargetTask(
            relatedTasks,
            targetTask,
            'delete'
          )

          // ganttTasksRefを更新
          ganttTasksRef.current = ganttTasksRef.current
            .filter((task) => task.id !== targetTask.id)
            .map((task) => {
              const updatedTask = updatedTasks.find(
                (updatedTask) => updatedTask.id === task.id
              )
              return updatedTask ?? task
            })

          // データソースのタスクを更新（対象タスクはremove、それ以外はupdate）
          ganttTaskDataSource.store().push(
            updatedTasks.map((task) => ({
              type: task.id === targetTask.id ? 'remove' : 'update',
              key: task.id,
              data: {
                ...task,
                startDate: task.startDate
                  ? dayjs(task.startDate).startOf('day').toDate()
                  : null, // 開始日を日の始まりに変換
                dueDate: task.dueDate
                  ? dayjs(task.dueDate).endOf('day').toDate()
                  : null, // 終了日を日の終わりに変換
                actualStartDate: task.actualStartDate
                  ? dayjs(task.actualStartDate).toDate()
                  : undefined,
                actualEndDate: task.actualEndDate
                  ? dayjs(task.actualEndDate).toDate()
                  : undefined,
                actualProgress: task.actualProgress ?? 0,
                version: task.version + 1, // バージョンをインクリメント
              },
            }))
          )

          return
        }

        // 関連するタスクと対象タスクを更新
        const updatedTasks = updateRelatedTasksByTargetTask(
          relatedTasks,
          targetTask,
          'update'
        )

        // ganttTasksRefを更新
        ganttTasksRef.current = ganttTasksRef.current.map((task) => {
          const updatedTask = updatedTasks.find(
            (updatedTask) => updatedTask.id === task.id
          )
          return updatedTask ?? task
        })

        // データソースのタスクを更新
        ganttTaskDataSource.store().push(
          updatedTasks.map((task) => ({
            type: 'update',
            key: task.id,
            data: {
              ...task,
              startDate: task.startDate
                ? dayjs(task.startDate).startOf('day').toDate()
                : undefined, // 開始日を日の始まりに変換
              dueDate: task.dueDate
                ? dayjs(task.dueDate).endOf('day').toDate()
                : undefined, // 終了日を日の終わりに変換
              actualStartDate: task.actualStartDate
                ? dayjs(task.actualStartDate).toDate()
                : undefined,
              actualEndDate: task.actualEndDate
                ? dayjs(task.actualEndDate).toDate()
                : undefined,
              actualProgress: task.actualProgress ?? 0,
              version: task.version + 1, // バージョンをインクリメント
            },
          }))
        )
      } else if (taskEventMessage.eventType === NotifyEventType.Deleted) {
        // 関連するすべてのタスクを検索
        const relatedTasks = findRelatedTasks(
          taskEventMessage.id,
          ganttTasksRef.current
        )

        // データソースからタスクを取得
        const targetTask = GanttRef.current?.instance.getTaskData(
          taskEventMessage.id
        )
        // 通常ありえないが、targetTaskが存在しない場合はリロードだけ行う
        if (!targetTask) {
          ganttTaskDataSource.reload()
          return
        }

        // 関連するタスクと対象タスクを更新
        const updatedTasks = updateRelatedTasksByTargetTask(
          relatedTasks,
          targetTask,
          'delete'
        )

        // ganttTasksRefを更新
        // 削除するタスク自身か、削除するタスクの子タスクは除外し、それ以外のタスクは更新された内容で上書きする
        ganttTasksRef.current = ganttTasksRef.current
          .filter(
            (task) =>
              task.id !== taskEventMessage.id &&
              task.parentTaskId !== taskEventMessage.id
          )
          .map((task) => {
            const updatedTask = updatedTasks.find(
              (updatedTask) => updatedTask.id === task.id
            )
            return updatedTask ?? task
          })

        // データソースのタスクを更新
        ganttTaskDataSource.store().push(
          updatedTasks.map((task) => ({
            type: task.id === taskEventMessage.id ? 'remove' : 'update',
            key: task.id,
            data: {
              ...task,
              startDate: task.startDate
                ? dayjs(task.startDate).startOf('day').toDate()
                : undefined, // 開始日を日の始まりに変換
              dueDate: task.dueDate
                ? dayjs(task.dueDate).endOf('day').toDate()
                : undefined, // 終了日を日の終わりに変換
              actualStartDate: task.actualStartDate
                ? dayjs(task.actualStartDate).toDate()
                : undefined,
              actualEndDate: task.actualEndDate
                ? dayjs(task.actualEndDate).toDate()
                : undefined,
              actualProgress: task.actualProgress ?? 0,
              version: task.version + 1, // バージョンをインクリメント
            },
          }))
        )
      } else {
        // イベントタイプがInserted、Updated、Deletedでない場合はガントチャートをリロードする
        ganttTaskDataSource.reload()
      }
    },
    [
      findRelatedTasks,
      ganttTaskDataSource,
      taskEventMessage,
      executionTargetTaskId,
      loggedInUser?.id,
      updateRelatedTasksByTargetTask,
    ],
    taskEventMessage,
    true
  )

  // ガントチャートの幅が変更されたときに、タスクリストの幅をリセットする
  useValueChangeEffect(
    () => {
      if (ganttWidth && GanttRef.current) {
        GanttRef.current.instance.resetOption('taskListWidth')
      }
    },
    [ganttWidth],
    ganttWidth
  )

  // 詳細表示列の表示・非表示が切り替わったときにガントチャートを再描画する
  useValueChangeEffect(
    () => {
      GanttRef.current?.instance.repaint()
    },
    [],
    isViewDetailColumn
  )

  return (
    <div
      className={classNames(
        'h-full',
        // NOTE: 全画面表示時は親の背景スタイルが継承されないため別途テーマ背景色を設定
        isFullScreen && 'crew-bg-default'
      )}
      ref={ganttChartContainerRef}
    >
      <div className="h-full" ref={ganttWrapperNodeRef}>
        <CrewGantt
          id="gantt-chart"
          ref={GanttRef}
          width={ganttWidth}
          height="100%"
          onTaskEditDialogShowing={handleTaskEditDialogShowing}
          onTaskInserting={handleTaskInsertDialogShowing}
          // Ganttのdx-splitter-borderが「z-index=997」でモーダルダイアログよりも上になってしまうため、Ganttコンテナにz-indexを設定する必要がある
          className="z-10"
          onScaleCellPrepared={handleScaleCellPrepared}
          taskListWidth={taskListWidth}
          onCustomCommand={handleContextMenuItemClick}
          onSelectionChanged={handleSelectionChanged}
          onTaskUpdating={handleTaskUpdating}
          onTaskDeleting={handleTaskDeleting}
        >
          {/* 右クリック時に表示されるコンテキストメニュー */}
          <ContextMenu items={getContextMenuItems()} />

          {/* 左側に表示する見出し。複数項目が必要な場合は必要分並べる。この項目でソート可 */}
          {/* 件名 */}
          <Column
            dataField="subject"
            caption={t('label.subject')}
            width={320}
          />
          {/* 関連先 */}
          <Column
            dataField="relatedItemName"
            caption={t('label.relatedItem')}
            width={200}
          />
          {/* 開始日 */}
          <Column
            dataField="startDate"
            caption={
              isViewDetailColumn
                ? t('label.scheduledStartDate')
                : t('label.startDate')
            }
            cellRender={dateFormatter}
            width={90}
          >
            <HeaderFilter dataSource={customizeHeaderFilterDateData} />
          </Column>
          {/* 期限 */}
          <Column
            dataField="dueDate"
            caption={
              isViewDetailColumn
                ? t('label.scheduledEndDate')
                : t('label.dueDate')
            }
            cellRender={dateFormatter}
            width={90}
          >
            <HeaderFilter dataSource={customizeHeaderFilterDateData} />
          </Column>
          {/* 予定作業時間 */}
          <Column
            dataField="estimatedWorkTimes"
            caption={t('label.scheduledWorkingTime')}
            visible={isViewDetailColumn}
            width={90}
            alignment="right"
            cssClass="filter-icon-alignment-right"
            calculateCellValue={calculateEstimatedWorkTimesCellValue}
          />
          {/* 実績開始日 */}
          <Column
            dataField="actualStartDate"
            caption={t('label.actualStartDate')}
            cellRender={dateFormatter}
            visible={isViewDetailColumn}
            width={90}
          >
            <HeaderFilter dataSource={customizeHeaderFilterDateData} />
          </Column>
          {/* 実績終了日 */}
          <Column
            dataField="actualEndDate"
            caption={t('label.actualEndDate')}
            cellRender={dateFormatter}
            visible={isViewDetailColumn}
            width={90}
          >
            <HeaderFilter dataSource={customizeHeaderFilterDateData} />
          </Column>
          {/* 実績作業時間 */}
          <Column
            dataField="actualWorkTimes"
            caption={t('label.actualWorkingTime')}
            visible={isViewDetailColumn}
            width={90}
            alignment="right"
            cssClass="filter-icon-alignment-right"
            calculateCellValue={calculateActualWorkTimesCellValue}
          />
          {/* 進捗率 */}
          <Column
            dataField="actualProgress"
            caption={t('label.progressRate')}
            visible={isViewDetailColumn}
            width={90}
            cellRender={renderProgressRate}
            cssClass="filter-icon-alignment-right"
          />
          {/* ステータス */}
          <Column
            dataField="taskState"
            caption={t('label.state')}
            visible={isViewDetailColumn}
            width={120}
            cellRender={renderTaskState}
            calculateCellValue={calculateTaskStateCellValue}
            cssClass="filter-icon-alignment-right"
          />

          {/* 以下、画面上には表示しないが、各種処理で必要なので定義しておく */}
          {/* エンティティレコードID */}
          <Column dataField="entityRecordId" visible={false} />
          {/* バージョン */}
          <Column dataField="version" visible={false} />

          {/*
            taskの定義
            https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxGantt/Configuration/tasks/
          */}
          <Tasks
            dataSource={ganttTaskDataSource}
            keyExpr="id"
            parentIdExpr="parentTaskId"
            titleExpr="subject"
            startExpr="startDate"
            endExpr="dueDate"
            progressExpr="actualProgress"
          />

          {/*
            task間の依存関係の定義
            https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxGantt/Configuration/dependencies/
          */}
          <Dependencies
            dataSource={taskDependencyDataSource}
            keyExpr="id"
            predecessorIdExpr="predecessorTaskId"
            successorIdExpr="successorTaskId"
            typeExpr="dependencyType"
          />

          {/*
            ツールバー設定
            https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxGantt/Configuration/toolbar/
          */}
          <Toolbar>
            <Item name="collapseAll" />
            <Item name="expandAll" />
            <Item name="separator" />
            <Item widget="dxButton" options={zoomInButtonOptions} />
            <Item widget="dxButton" options={zoomOutButtonOptions} />
            <Item widget="dxButton" options={fullScreenButtonOptions} />
            <Item name="separator" />
            <Item
              name="addTask"
              render={renderAddTaskButton}
              locateInMenu="auto"
            />
            <Item
              widget="dxButton"
              render={renderAddSubtaskButton}
              locateInMenu="auto"
            />
            <Item
              widget="dxButton"
              render={renderDeleteTaskButton}
              locateInMenu="auto"
            />
            <Item name="separator" />
            <Item
              widget="dxButton"
              render={renderTodayButton}
              locateInMenu="auto"
            />
            <Item
              widget="dxButton"
              render={renderDisplayDetailButton}
              locateInMenu="auto"
            />
          </Toolbar>

          {/*
            ソート設定
            https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxGantt/Configuration/sorting/
          */}
          <Sorting
            mode="multiple"
            showSortIndexes={true}
            ascendingText={t('label.ascending')}
            descendingText={t('label.descending')}
            clearText={t('label.clear')}
          />

          {/*
            編集できる項目の設定
            https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxGantt/Configuration/editing/
          */}
          <Editing
            enabled // 編集を有効化
            // デフォルトで全ての項目が編集可能
            allowResourceAdding={false}
            allowResourceDeleting={false}
            allowResourceUpdating={false}
            allowTaskResourceUpdating={false}
            // 依存関係は閲覧専用とし編集不可とする
            allowDependencyAdding={false}
            allowDependencyDeleting={false}
          />

          {/*
            編集時の挙動の制御
            https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxGantt/Configuration/validation/
          */}
          <Validation
            autoUpdateParentTasks={false} // 子タスクの変更で親タスクの開始日・終了日を自動更新しない
            validateDependencies={false} // タスクの移動に合わせて依存のあるタスクも移動する、または修正を拒否する
            enablePredecessorGap // 先行タスクの移動でタスク間の隙間を修正可能にする。デフォルトでは隙間を維持したまま後続タスクも移動する
          />

          {/*
            ヘッダーにフィルターを設定
            https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxGantt/Configuration/headerFilter/
          */}
          <HeaderFilter visible={true} />
        </CrewGantt>

        <TaskEntryDialog
          entryFormMode={taskEntryFormMode}
          title={
            taskEntryFormMode === TaskEntryFormMode.Edit
              ? t('label.editTaskTitle')
              : t('label.addTaskTitle')
          }
          onSubmit={handleTaskEntryDialogSubmit}
          onDeleted={
            taskEntryFormMode === TaskEntryFormMode.Edit
              ? handleTaskDeleted
              : undefined
          }
          isOpen={isTaskEntryDialogOpen}
          onClose={closeTaskEntryDialog}
          taskId={executionTargetTaskId}
          parentTaskId={parentTaskId}
          projectId={projectIdForTaskEntryDialog}
        />

        {/* 削除確認ダイアログ */}
        <CrewConfirmDialog
          isOpen={isConfirmDialogOpen}
          message={confirmMessage}
          onPermitButtonClick={handleDeletePermitButtonClick}
          onCancelButtonClick={closeConfirmDialog}
        />
      </div>
    </div>
  )
})
